import { hashTexto, limitar, normalizar, pseudoAleatorio } from '../models/deterministic.model';

/**
 * El árbol del programa: qué zona cuelga de qué región, en qué estado está
 * cada plaza y cuánto NPS hace cada sucursal.
 *
 * Vive aparte de `dimensions.data.ts` porque lo necesitan los dos lados: los
 * archivos de datos, para escribir filas que ya vengan colocadas en el árbol, y
 * el catálogo, para colocar las sucursales que ninguna tabla menciona. Tenerlo
 * en un solo sitio es lo que evita que la misma tienda salga en dos zonas.
 *
 * **Es dato sintético.** La estructura —cinco regiones, doce zonas, los nombres
 * de las plazas— viene del programa; el reparto y las cifras, de aquí.
 */

/* -------------------------------------------------------------------------- */
/*                              ANCHOR el arbol                               */
/* -------------------------------------------------------------------------- */

/**
 * Cada zona cuelga de UNA región.
 *
 * En los datos de origen la misma zona aparecía bajo regiones distintas según
 * la tabla, así que filtrar por región devolvía sucursales de otra. Aquí la
 * pertenencia es única y el filtro cierra.
 */
export const ZONAS_POR_REGION: Record<string, Array<string>> = {
  'ALFONSO VEGA':      ['ALICIA VALENCIA', 'ELEAZAR GARCIA', 'ENRIQUE GONZALEZ'],
  'ATANACIO MENDOZA':  ['ERIKA MARTINEZ', 'GUILLERMO MARTINEZ'],
  'LEONARDO MAXIMO':   ['ISMAEL FERMIN', 'JAIME BALLINA', 'JESUS ROSAS'],
  'MARTIN MARIN':      ['RODRIGO ROLDAN', 'SILVIA ALONSO'],
  'OTILIO MACHUCA':    ['VERONICA HERRERA', 'VERONICA LOPEZ'],
};

export const REGION_DE_ZONA: Record<string, string> = Object.fromEntries(
  Object.entries(ZONAS_POR_REGION).flatMap(([region, zonas]) => zonas.map((zona) => [zona, region])),
);

const ZONAS_LISTA: Array<string> = Object.values(ZONAS_POR_REGION).flat().sort();

/** Zona de una sucursal. Del nombre, para que no dependa de qué tabla la cite. */
export const zonaDe = (sucursal: string): string =>
  ZONAS_LISTA[hashTexto(`Z${normalizar(sucursal)}`) % ZONAS_LISTA.length];

export const regionDe = (sucursal: string): string => REGION_DE_ZONA[zonaDe(sucursal)];

/* -------------------------------------------------------------------------- */
/*                       ANCHOR el estado de cada plaza                       */
/* -------------------------------------------------------------------------- */

/**
 * Los nombres de sucursal son poblaciones mexicanas de verdad, así que colgar
 * cada una del estado que le toca cuesta lo mismo que sortearla y evita que
 * «SUCURSAL ACAPULCO» aparezca al filtrar por Jalisco. Etla, Fortín y Tlaxcala
 * caen fuera de los diez estados del programa y se cuelgan del vecino.
 */
const PLAZAS_POR_ESTADO: Record<string, Array<string>> = {
  'CDMX': [
    'AJUSCO', 'ALTA TENSION', 'ALTA TENSION 2', 'ALTA TENSION 3', 'ALVARO OBREGON',
    'ARAGON', 'AVENIDA CENTRAL', 'AZCAPOTZALCO', 'AZTAHUACAN', 'BALBUENA',
    'BOSQUES DE ARAGON', 'CANDELARIA', 'CENTRO HISTORICO', 'CHAPULTEPEC', 'COAPA',
    'COPILCO', 'COYOACAN', 'CUAJIMALPA', 'CUAUHTEMOC', 'CULHUACAN', 'DOCTORES',
    'EJE CENTRAL', 'EL ROSARIO', 'ERMITA', 'ESCANDON', 'GUSTAVO A MADERO',
    'GUSTAVO A. MADERO', 'IZTACALCO', 'IZTAPALAPA', 'IZTAPALAPA ORIENTE', 'JUAREZ',
    'LA VILLA', 'LINDAVISTA', 'MAGDALENA', 'MAGDALENA CONTRERAS', 'MARTIN CARRERA',
    'MERCED', 'MILPA ALTA', 'MIXCOAC', 'MOLINO DE ROSAS', 'OBRERA', 'PANTITLAN',
    'PASEOS DE CHURUBUSCO', 'PATRIOTISMO', 'PEDREGAL', 'PENON', 'PERALVILLO', 'PIEDAD',
    'PORTALES', 'REFORMA', 'ROMA SUR', 'SAN JERONIMO', 'SANTA FE', 'SANTA JULIA',
    'SANTA MARTHA', 'TACUBA', 'TACUBAYA', 'TALISMAN', 'TALLERES', 'TLAHUAC', 'TLALPAN',
    'VALLEJO', 'VENUSTIANO CARRANZA', 'VILLA COAPA', 'XOCHIMILCO',
  ],
  'ESTADO DE MÉXICO': [
    'ACAMBAY', 'ACOLMAN', 'ALMOLOYA', 'AMATEPEC', 'AMECAMECA', 'ARBOLEDAS', 'ATIZAPAN',
    'ATIZAPAN 2', 'ATLACOMULCO', 'AXAPUSCO', 'BUENAVISTA', 'CAMPESTRE', 'CANOAS',
    'CASA BLANCA', 'CAZADOR', 'CAZADOR NAUCALPAN', 'CERRO GORDO', 'CHALCO', 'CHALCO 2',
    'CHICOLOAPAN', 'CHICONCUAC', 'CHIMALHUACAN', 'COACALCO', 'CUAUTITLAN',
    'CUAUTITLAN IZCALLI', 'ECATEPEC', 'ECATEPEC 2', 'ECATEPEC NORTE', 'ECATZINGO',
    'EL ORO', 'GUADALUPE', 'HUEHUETOCA', 'HUIXQUILUCAN', 'IXTAPALUCA', 'IXTAPAN',
    'IXTLAHUACA', 'JALTENCO', 'JILOTEPEC', 'JIQUIPILCO', 'JOCOTITLAN', 'LA PAZ',
    'LERMA', 'LOMAS VERDES', 'LOS REYES', 'MALINALCO', 'METEPEC', 'MEXICALTZINGO',
    'NAUCALPAN', 'NAUCALPAN 2', 'NEZAHUALCOYOTL', 'NICOLAS ROMERO', 'NOPALTEPEC',
    'OCOYOACAC', 'OJO DE AGUA', 'OTUMBA', 'PERINORTE', 'PROGRESO', 'SAN AGUSTIN',
    'SAN ANTONIO', 'SAN BUENAVENTURA', 'SAN CRISTOBAL', 'SAN MATEO', 'SANTA CLARA',
    'TECAMAC', 'TEJUPILCO', 'TENANCINGO', 'TENANGO', 'TEOLOYUCAN', 'TEOTIHUACAN',
    'TEPOTZOTLAN', 'TEXCOCO', 'TEZOYUCA', 'TLALNEPANTLA', 'TOLLOCAN', 'TOLUCA 1',
    'TOLUCA 2', 'TOLUCA CENTRO', 'TOLUCA NORTE', 'TULTEPEC', 'TULTITLAN',
    'VALLE DE BRAVO', 'VALLE DE CHALCO', 'VALLE DORADO', 'VILLA DEL CARBON',
    'XALOSTOC', 'ZINACANTEPEC', 'ZUMPANGO',
  ],
  'GUANAJUATO': [
    'ACAMBARO', 'ALLENDE', 'APASEO', 'CELAYA', 'CELAYA 2', 'DOLORES HIDALGO',
    'GUANAJUATO CAPITAL', 'IRAPUATO', 'LEON', 'LEON 2', 'LEON CENTRO', 'LEON ORIENTE',
    'MARIANO ESCOBEDO LEON', 'MOROLEON', 'PENJAMO', 'SALAMANCA', 'SAN FELIPE',
    'SAN FRANCISCO DEL RINCON', 'SAN LUIS DE LA PAZ', 'SAN MIGUEL',
    'SAN MIGUEL DE ALLENDE', 'SILAO', 'VALLE DE SANTIAGO',
  ],
  'GUERRERO': [
    'ACAPULCO', 'ACAPULCO COSTERA', 'ACAPULCO DIAMANTE', 'ALTAMIRA', 'ARCELIA',
    'CHILPANCINGO', 'COYUCA', 'IGUALA', 'IGUALA 2', 'OMETEPEC', 'TAXCO', 'TLAPA',
    'ZIHUATANEJO',
  ],
  'HIDALGO': [
    'ACTOPAN', 'AHUEHUETES', 'AJACUBA', 'APAN', 'ATOTONILCO', 'ATOTONILCO EL GRANDE',
    'CARDONAL', 'CIUDAD SAHAGUN', 'HUEJUTLA', 'HUICHAPAN', 'IXMIQUILPAN', 'JACALA',
    'METZTITLAN', 'MINERAL DE LA REFORMA', 'MINERAL DEL MONTE', 'MIXQUIAHUALA',
    'MOLANGO', 'OMITLAN', 'PACHUCA', 'PACHUCA 2', 'PACHUCA CENTRO', 'PACHUCA NORTE',
    'PACHUCA SUR', 'PROGRESO DE OBREGON', 'SAN SALVADOR', 'SANTIAGO TULANTEPEC',
    'SINGUILUCAN', 'TASQUILLO', 'TENANGO DE DORIA', 'TEPEAPULCO', 'TEPEJI',
    'TEPEJI DEL RIO', 'TIZAYUCA', 'TIZAYUCA II', 'TLAXCOAPAN', 'TULA',
    'TULA DE ALLENDE', 'TULANCINGO', 'TULANCINGO ORIENTE', 'ZACUALTIPAN', 'ZIMAPAN',
  ],
  'JALISCO': [
    'AMECA', 'ARANDAS', 'AUTLAN', 'CHAPALA', 'CIUDAD GUZMAN', 'EL SALTO',
    'GUADALAJARA', 'GUADALAJARA 2', 'GUADALAJARA CENTRO', 'GUADALAJARA NORTE',
    'LAGOS DE MORENO', 'OCOTLAN', 'PROFETA', 'PUERTO VALLARTA', 'TEPATITLAN',
    'TLAJOMULCO', 'TLAQUEPAQUE', 'TONALA', 'ZAPOPAN',
  ],
  'MICHOACAN': [
    'APATZINGAN', 'MORELIA', 'MORELIA 2', 'PARAISO', 'URUAPAN', 'ZAMORA',
  ],
  'MORELOS': [
    'CUAUTLA', 'CUERNAVACA', 'CUERNAVACA 2', 'CUERNAVACA CENTRO', 'CUERNAVACA NORTE',
    'EMILIANO ZAPATA', 'JIUTEPEC', 'OAXTEPEC', 'PUENTE DE IXTLA', 'TEMIXCO',
    'XOCHITEPEC', 'YAUTEPEC', 'ZACATEPEC',
  ],
  'PUEBLA': [
    'APIZACO', 'ATLIXCO', 'AV. 11 SUR', 'CALPULALPAN', 'CHOLULA', 'COATEPEC',
    'COATZINGO', 'ETLA', 'FORTIN', 'HUEJOTZINGO', 'PUEBLA 2', 'PUEBLA CENTRO',
    'PUEBLA SUR', 'RIO FRIO', 'SAN MARTIN', 'TECAMACHALCO', 'TEHUACAN', 'TLAXCALA',
  ],
  'QUERETARO': [
    'AMEALCO', 'ANAHUAC', 'CADEREYTA', 'COLON', 'CONSTITUYENTES', 'CORREGIDORA',
    'EL MARQUES', 'EZEQUIEL MONTES', 'FRESNILLO', 'PEDRO ESCOBEDO', 'QUERETARO',
    'QUERETARO 2', 'QUERETARO CENTRO', 'QUERETARO SUR', 'SAN JUAN DEL RIO',
    'TEQUISQUIAPAN',
  ],
};

const ESTADOS_LISTA: Array<string> = Object.keys(PLAZAS_POR_ESTADO);

const ESTADO_POR_PLAZA: Map<string, string> = new Map(
  Object.entries(PLAZAS_POR_ESTADO).flatMap(([estado, plazas]) =>
    plazas.map((plaza) => [plaza, estado] as [string, string])),
);

/** Estado de una sucursal; las que no están en el maestro se reparten por hash. */
export const estadoDe = (sucursal: string): string => {
  const plaza = normalizar(sucursal).replace(/^SUCURSAL /, '');

  return ESTADO_POR_PLAZA.get(plaza)
    ?? ESTADOS_LISTA[hashTexto(`E${normalizar(sucursal)}`) % ESTADOS_LISTA.length];
};

/* -------------------------------------------------------------------------- */
/*                          ANCHOR NPS por sucursal                           */
/* -------------------------------------------------------------------------- */

/**
 * Cuánto suma o resta cada región al NPS de sus sucursales.
 *
 * Sin este sesgo, promediar sesenta sucursales por región converge al mismo
 * número y el ranking por región sale plano —tres regiones empatadas—, que se
 * lee como un error y no como un empate.
 */
const OFFSET_REGION: Record<string, number> = {
  'ALFONSO VEGA':       3,
  'ATANACIO MENDOZA':  -2,
  'LEONARDO MAXIMO':    6,
  'MARTIN MARIN':      -5,
  'OTILIO MACHUCA':     0,
};

/**
 * NPS de una sucursal. **Única fuente**: la misma tienda trae esta cifra en el
 * NPS por sucursal del Pulso, en el ranking de Explorar y en el CSAT por
 * tienda, que es lo primero que revisa quien conoce su sucursal.
 *
 * Dos tiradas promediadas y no una: una uniforme deja tantas tiendas en 41 como
 * en 72 y el ranking se ve plano. Promediar dos da una campana.
 */
export const npsDeSucursal = (sucursal: string): number => {
  const rnd = pseudoAleatorio(hashTexto(normalizar(sucursal)));
  const base = 43 + ((rnd() + rnd()) / 2) * 58;

  return limitar(Math.round(base + OFFSET_REGION[regionDe(sucursal)]), 41, 95);
};

/**
 * Detractores / pasivos / promotores coherentes con un NPS dado: los tres suman
 * 100 y `nps = promotores - detractores`.
 *
 * El grado de libertad es el pasivo, y su techo es `100 - nps`: con NPS 89 sólo
 * quedan once puntos para repartir entre pasivos y el doble de detractores. Se
 * toma una fracción de ese techo y no un valor absoluto, que es lo que dejaba
 * tiendas de NPS alto con 1% de pasivos.
 */
export const desgloseDe = (nps: number, semilla: string): {
  detractores: number;
  pasivos: number;
  promotores: number;
} => {
  const rnd = pseudoAleatorio(hashTexto(semilla));
  let pasivos = Math.round((100 - nps) * (0.35 + rnd() * 0.35));

  if ((100 - pasivos - nps) % 2) {
    pasivos++;
  }

  const detractores = Math.max(0, (100 - pasivos - nps) / 2);
  const promotores = detractores + nps;

  return { detractores, pasivos: 100 - detractores - promotores, promotores };
};
